import { useEffect, useMemo, useState } from 'react'
import { BadgeDollarSign, Ban, CircleAlert, Percent, Receipt, RotateCcw, Search, ShieldAlert, TrendingUp } from 'lucide-react'
import { api, brl, formatDate, integer } from '../api'
import { Empty, Loading } from '../components/Loading'
import { MetricCard } from '../components/MetricCard'
import { Modal } from '../components/Modal'
import { Pagination, usePagedItems } from '../components/Pagination'
import { HorizontalScroll } from '../components/HorizontalScroll'
import type { Finance, FinanceOrder } from '../types'
import { Bars, PageHead, PanelTitle, statusLabel } from './DashboardPage'

// O histórico só mostra pagamentos que efetivamente foram cobrados em algum
// momento (aprovados, mesmo que depois estornados) — igual ao que o backend
// usa para compor os totais e os gráficos de faturamento/estornos/taxas.
const REFUND_STATUSES = ['refunded', 'charged_back']
const HISTORY_STATUSES = ['approved', ...REFUND_STATUSES]

export function FinancePage() {
  const [data, setData] = useState<Finance | null>(null)
  const [status, setStatus] = useState('all')
  const [query, setQuery] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [cancelling, setCancelling] = useState<FinanceOrder | null>(null)

  const load = () => {
    setError('')
    api<Finance>('/finance').then(setData).catch(reason => setError(reason.message))
  }

  useEffect(load, [])

  const rows = useMemo(() => (data?.orders || []).filter(order =>
    HISTORY_STATUSES.includes(order.status)
    && (status === 'all' || (status === 'refunded' ? REFUND_STATUSES.includes(order.status) : order.status === status))
    && `${order.customer_name} ${order.customer_email} ${order.id} ${order.payment_id}`.toLowerCase().includes(query.toLowerCase())
  ), [data, status, query])
  const { pageItems, page, totalPages, setPage } = usePagedItems(rows)

  if (error && !data) return <div className="notice error"><CircleAlert/>{error}</div>
  if (!data) return <Loading label="Consolidando informações financeiras…"/>

  const mercadoPagoConfigured = Boolean(data.provider?.mercado_pago_configured)
  const metrics = [
    { label: 'Receita líquida', value: brl.format(data.totals.net), detail: 'após taxas registradas', icon: BadgeDollarSign, featured: true },
    { label: 'Receita aprovada', value: brl.format(data.totals.approved), detail: 'pagamentos', icon: BadgeDollarSign },
    { label: 'Pagamentos', value: integer.format(data.totals.approved_orders), detail: 'pagamentos aprovados', icon: Receipt },
    { label: 'Taxas', value: brl.format(data.totals.fees), detail: 'retidas nos pagamentos', icon: Percent },
    { label: 'Estornos', value: brl.format(data.totals.refunded), detail: 'reembolsos e chargebacks', icon: RotateCcw },
    { label: 'Ticket médio', value: brl.format(data.totals.average_ticket), detail: 'por pagamento aprovado', icon: BadgeDollarSign },
  ]

  return <>
    <PageHead eyebrow="RECEITA E PAGAMENTOS" title="Faturamento" text="Acompanhe faturamento, pendências, cancelamentos e estornos."/>
    {!mercadoPagoConfigured && <div className="notice info"><ShieldAlert/><span>Para cancelar pagamentos, configure o segredo <strong>MERCADO_PAGO_ACCESS_TOKEN</strong> no ambiente de Produção do Cloudflare e faça um novo deploy.</span></div>}
    {error && <div className="notice error"><CircleAlert/>{error}</div>}
    {success && <div className="notice success"><Ban/><span>{success}</span><button type="button" onClick={() => setSuccess('')} aria-label="Fechar mensagem">×</button></div>}
    <section className="metric-grid finance-metrics" aria-label="Indicadores financeiros">{metrics.map(item => <MetricCard key={item.label} {...item}/>)}</section>
    <section className="finance-charts-row">
      <article className="panel chart-panel finance-chart"><PanelTitle icon={BadgeDollarSign} title="Receita líquida por mês" text="Pagamentos aprovados, já descontadas as taxas"/><Bars data={data.net_history} currency/></article>
      <article className="panel chart-panel finance-chart"><PanelTitle icon={TrendingUp} title="Receita aprovada por mês" text="Valor total cobrado, com taxas e sem descontar estornos"/><Bars data={data.revenue_history} currency/></article>
      <article className="panel chart-panel finance-chart"><PanelTitle icon={Percent} title="Taxas por mês" text="Taxas retidas nos pagamentos"/><Bars data={data.fee_history} currency/></article>
      <article className="panel chart-panel finance-chart"><PanelTitle icon={RotateCcw} title="Estornos por mês" text="Reembolsos e chargebacks"/><Bars data={data.refund_history} currency/></article>
    </section>
    <div className="toolbar">
      <label className="search-field"><Search/><input value={query} onChange={event => setQuery(event.target.value)} placeholder="Buscar cliente, pedido ou pagamento" aria-label="Buscar pagamento"/></label>
      <label><span>Status</span><select value={status} onChange={event => setStatus(event.target.value)}>
        <option value="all">Todos</option><option value="approved">Aprovados</option><option value="refunded">Estornados</option>
      </select></label>
    </div>
    <section className="panel table-panel">{rows.length ? <>
      <HorizontalScroll label="Pagamentos: tabela com rolagem horizontal" className="table-wrap finance-table"><table>
        <thead><tr><th>Cliente</th><th>Pedido</th><th>Pagamento MP</th><th>Status</th><th>Método</th><th>Cupom</th><th>Valor</th><th>Taxa</th><th>Líquido</th><th>Data</th><th>Ações</th></tr></thead>
        <tbody>{pageItems.map(order => <tr key={order.id}>
          <td><strong>{order.customer_name || 'Cliente'}</strong><small>{order.customer_email || order.uid_usuario || '—'}</small></td>
          <td><code>{order.id}</code></td>
          <td><code>{order.payment_id || '—'}</code></td>
          <td><span className={`payment-badge ${order.status}`}>{statusLabel(order.status)}</span></td>
          <td>{paymentMethod(order.payment_method)}</td><td>{order.coupon_code || '—'}</td>
          <td><strong>{brl.format(order.amount)}</strong></td>
          <td>{order.fee_amount === undefined ? '—' : brl.format(order.fee_amount)}</td>
          <td>{order.net_amount === undefined ? '—' : brl.format(order.net_amount)}</td>
          <td>{formatDate(order.created_at)}</td>
          <td><PaymentAction order={order} configured={mercadoPagoConfigured} cancel={() => setCancelling(order)}/></td>
        </tr>)}</tbody>
      </table></HorizontalScroll>
      <Pagination page={page} totalPages={totalPages} onChange={setPage}/>
    </> : <Empty>Nenhum pagamento corresponde aos filtros.</Empty>}</section>
    {cancelling && <CancelPaymentModal
      order={cancelling}
      close={() => setCancelling(null)}
      cancelled={() => {
        setCancelling(null)
        setSuccess(`Cobrança do pedido ${cancelling.id} cancelada com sucesso.`)
        load()
      }}
    />}
  </>
}

function PaymentAction({ order, configured, cancel }: { order: FinanceOrder; configured: boolean; cancel: () => void }) {
  if (order.cancellable) return <button type="button" className="table-action danger" onClick={cancel} disabled={!configured} title={configured ? 'Cancelar cobrança no Mercado Pago' : 'Configure o Access Token no Cloudflare'}><Ban/>Cancelar</button>
  return <span className="action-unavailable">—</span>
}

function CancelPaymentModal({ order, close, cancelled }: { order: FinanceOrder; close: () => void; cancelled: () => void }) {
  const [working, setWorking] = useState(false)
  const [error, setError] = useState('')

  const confirm = async () => {
    setWorking(true)
    setError('')
    try {
      await api(`/finance/orders/${encodeURIComponent(order.id)}/cancel`, { method: 'POST' })
      cancelled()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Não foi possível cancelar o pagamento.')
      setWorking(false)
    }
  }

  return <Modal title="Cancelar pagamento pendente" close={close}>
    <div className="confirm-content payment-cancel-confirm">
      <span className="confirm-icon danger"><Ban/></span>
      <div><h3>Confirmar cancelamento?</h3><p>O painel procurará um pagamento associado e, se ele ainda não existir, encerrará a preferência de checkout.</p></div>
      <dl>
        <div><dt>Cliente</dt><dd>{order.customer_name || order.customer_email || '—'}</dd></div>
        <div><dt>{order.payment_id ? 'Pagamento' : 'Preferência'}</dt><dd>{order.payment_id || order.preference_id || '—'}</dd></div>
        <div><dt>Valor</dt><dd>{brl.format(order.amount)}</dd></div>
      </dl>
      <div className="notice info compact"><CircleAlert/><span>Somente pagamentos ainda não aprovados podem ser cancelados. Esta operação ficará registrada na Auditoria.</span></div>
      {error && <div className="form-error" role="alert">{error}</div>}
      <div className="modal-actions"><button type="button" className="secondary" onClick={close}>Voltar</button><button type="button" className="danger-action" onClick={confirm} disabled={working}>{working ? 'Cancelando…' : 'Confirmar cancelamento'}</button></div>
    </div>
  </Modal>
}

function paymentMethod(method?: string) {
  return ({ pix: 'Pix', card: 'Cartão', credit_card: 'Cartão', coupon: 'Cupom 100%' } as Record<string, string>)[method || ''] || method || '—'
}

